/**
 * Every hard-coded string in the Climb Plan PDF, mirroring
 * careerPlanPDFTexts.ts.
 *
 * ASCII ONLY. A character outside the embedded font subset renders as nothing
 * at all, with no warning and no error — so no curly quotes, no en dashes, no
 * arrows, and `|` rather than the middot the review uses between grade and ID.
 */
export const climbPlanPDFTexts = {
  header: {
    /** Spelled out in the top right, where the date used to sit. */
    studentIdLabel: "Student ID:",
    updatedLabel: "Updated",
  },

  footer: {
    documentTitle: "Climb Plan",
    page: (current: number, total: number) => `Page ${current} of ${total}`,
  },

  identity: {
    /** "<Name>'s Climb Plan" - the document's only title. */
    title: (name: string) => `${name}'s Climb Plan`,
    myCore: "My Core Personality Types",
    myWorkValues: "My Work Values",
    signedBy: "Signed by",
  },

  /**
   * Page one: the hero, the section bands, and the cards inside them.
   *
   * The BAND headings are the uppercase ones that sit outside a card -
   * "MY PROFILE", "HIGH SCHOOL PRIORITIES". The CARD labels are the small
   * overlines inside one. A card's label is sentence case because it names a
   * field; a band's is uppercase because it names a part of the document.
   */
  summary: {
    topHat: "TOP HAT",
    myProfile: "MY PROFILE",
    highInterestCore: "HIGH INTEREST CORE PERSONALITY",
    workValues: "WORK VALUES",
    highSchoolPriorities: "HIGH SCHOOL PRIORITIES",
    skill: "SKILL",
    afterHighSchoolBand: "AFTER HIGH SCHOOL",
    reflection: "EXPLAIN YOUR THINKING",

    highSchool: "High School",
    program: "Program",
    afterHighSchool: "After High School",
    keyToSuccess: "Key to Success",
    strength: "Strength",
    /** One goal or several - the pill says which. */
    goals: (count: number) => `Goal${count === 1 ? "" : "s"}`,
  },

  sections: {
    myProfile: "MY CORE PERSONALITY & WORK VALUES",
    myProfileSubtitle:
      "The climber's own results, as their Profile reports them.",
    /**
     * What the two measures ARE, for a reader meeting them for the first time.
     *
     * Authored here rather than read from the CMS facet types: only one of the
     * two carries a description there (Core Personality's is empty), so half
     * this box would be blank.
     */
    whatTheseAre: [
      {
        name: "Core Personality",
        text: "The kinds of work a person is naturally drawn to. Everyone is a mix of all six types - the ones scored High Interest are where this climber's interests are strongest.",
      },
      {
        name: "Work Values",
        text: "What makes work feel rewarding. A climber's top values point at the conditions they want from a career rather than the tasks they will do.",
      },
    ],

    topHat: "TOP HAT",
    topHatSubtitle: "The career this plan is built around.",
    corePersonalityTypes: "Core Personality Types",
    corePersonalitySubtitle: "What kind of person enjoys this work?",
    workValues: "Work Values",
    workValuesSubtitle: "What makes this work rewarding?",
    perksAndQuirks: "Perks & Quirks",
    personalityBadges: "Personality Badges",

    connectProfile: "CONNECT YOUR PROFILE",
    connectProfileSubtitle:
      "The parts of this climber's Profile, such as Building Blocks, that fit with their Top Hat.",

    highSchoolPriorities: "HIGH SCHOOL PRIORITIES",
    highSchoolPrioritiesSubtitle:
      "The high school program and the key to success chosen to reach this career goal.",
    availableSchools: "Available at these High Schools",
    availableCredentials: "Available Credentials",
    sampleCourses: "Sample Course Sequence",
    otherHats: "Other Hats relating to this Program",

    programSpotlight: "PROGRAM SPOTLIGHT",

    keysToSuccess: "KEYS TO SUCCESS",
    keysToSuccessSubtitle:
      "The habit this climber will focus on through high school.",

    ctsos: "CTSOS",
    ctsosSubtitle:
      "The Career and Technical Student Organizations attached to this program.",

    afterHighSchool: "AFTER HIGH SCHOOL",
    afterHighSchoolSubtitle:
      "The education and training paths this climber is thinking about for building the skills and experience this career asks for.",

    skillsToBuild: "SKILLS TO BUILD",
    skillsToBuildSubtitle:
      "The durable skills (PowerSkills) and technical skills (ProSkills) that are important for this Hat.",
    /** The chips on the cards, singular, as SkillCard's badges read. */
    strength: "Strength",
    goal: "Goal",

    shoutOuts: "SHOUT OUTS",
    shoutOutsSubtitle:
      "Recognition this climber has been given by their coaches.",
  },

  labels: {
    fit: "FIT",
    /** Over Connect Your Profile, naming which path the climber took. */
    coreConnected: "Core Connected",
    blocksConnected: "Building Blocks Connected",
    likes: "Likes:",
    dislikes: "Dislikes:",
    /** Over a shout-out whose skill the Hat still carries. */
    shoutOutFor: "For",
  },
} as const;
